import { Component, Input } from '@angular/core';
import { SituViewWebApiClient, Product2, FileVaultUploadRequest, FileVaultUploadCommitRequest, FileVaultCategory, AddFileVaultReferencesRequest, RemoveFileVaultReferencesRequest, CompanySubjectPair, GetFileVaultReferenceRelationsRequest, FileVaultReferenceRelation, GetAssetValidationRequest, AssetValidationRule, RuleResult, GetZippedFilesRequest, CompanyProductIdPair } from 'situview-webapi-client';
import { AuthService, BreadcrumbService, StateService, ConfigService, Utils, ConfirmDialogService } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FileVaultSelectorDialogComponent, FileVaultSelectorDialogData, FileVaultSelectorResultData, ProductSelectorDialogComponent, ProductSelectorDialogData, ProductSelectorResultData } from 'src/app/components';
import { INewProduct } from 'src/app/views';
import * as FileSaver from 'file-saver';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import * as SparkMD5 from 'spark-md5';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'file-vault-browser-tab',
  templateUrl: './file-vault-browser-tab.component.html',
  styleUrls: ['./file-vault-browser-tab.component.scss']
})

export class FileVaultBrowserTabComponent extends SituBaseComponent {
  @Input() product: INewProduct = new Product2();
  @Input() fileVaultCategory?: FileVaultCategory;
  @Input() validateAssets: boolean = false;

  readonly SituViewUrl: string;

  newFiles: NewFileDetails[] = [];
  existingFiles: ExistingFileDetails[] = [];

  validFileExtensions: string[] = [];

  productSelectorDialogRef: MatDialogRef<ProductSelectorDialogComponent> | undefined;
  fileVaultSelectorDialogRef: MatDialogRef<FileVaultSelectorDialogComponent> | undefined;

  private _loading = 0;
  public get loading(): number { return this._loading; }

  public set loading(val: number) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val != 0);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private configService: ConfigService,
    private situViewClient: SituViewWebApiClient,
    private http: HttpClient,
    private dialog: MatDialog,
    private confirmDialogService: ConfirmDialogService) {
      super(authService, stateService);

      this.SituViewUrl = configService.config.situViewWebApiBaseUrl;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.loadValidExtensions();
    this.loadFiles();
  }

  async onAddExistingReferenceItemClick(fileHashFilter: string | undefined = undefined): Promise<boolean> {

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
  
    const dialogWidth = `${Math.round((70 / 100) * screenWidth)}px`;
    const dialogHeight = `${Math.round((90 / 100) * screenHeight)}px`;

    var dialogData = new FileVaultSelectorDialogData();
    dialogData.companyId = this.product?.companyId!;
    dialogData.fileVaultCategory = this.fileVaultCategory!;
    dialogData.validFileExtensions = this.validFileExtensions;
    dialogData.fileHashFilter = fileHashFilter;

    this.fileVaultSelectorDialogRef = this.dialog.open(FileVaultSelectorDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: dialogData,
    });

    var data: FileVaultSelectorResultData = await this.fileVaultSelectorDialogRef.afterClosed().toPromise();

    if (!data) {
      // The user did not select an option
      return false;
    }

    var toAdd = data.fileVaultReferences.filter(x => !this.existingFiles.some(e => e.id === x.id));

    var addReferencesRequest = new AddFileVaultReferencesRequest();
    addReferencesRequest.fileVaultReferenceIds = toAdd.map(x => x.id!);
    addReferencesRequest.category = this.fileVaultCategory;
    var companySubjectPair = new CompanySubjectPair();
    companySubjectPair.companyId = this.product.companyId;
    companySubjectPair.subjectId = this.product.id;
    addReferencesRequest.subjects = [companySubjectPair];

    var addReferenceResponse = await this.situViewClient.addFileVaultReferences(addReferencesRequest);

    if (!addReferenceResponse.success) {
      throw new Error("Failed to add file to product");
    }

    this.existingFiles.push(...addReferenceResponse.fileVaultReferences! as ExistingFileDetails[]);

    return true;
  }

  buildValidFileTypesString() {
    if (this.validFileExtensions.length === 0) {
      return "";
    }

    return `${this.validFileExtensions.join(", ")}.`;
  }

  onAddNewReferenceItemClick() {
    document.getElementById("add-new-reference-item")?.click();
  }

  async dropHandler(ev: DragEvent) {
    //console.log("Items(s) dropped");
  
    // Prevent the file from being opened in the browser
    ev.preventDefault();

    if (!ev.dataTransfer || !ev.dataTransfer.items) {
      return;
    }

    var newFileDetailsCollection: NewFileDetails[] = [];
    var items = ev.dataTransfer.items;
    for (let item of items) {   
      //console.log(`item.type = ${item?.type}`);
      //console.log(`item.kind = ${item?.kind}`);
      if (item.kind === "file") {
        var file = item.getAsFile();
        if (!file) {
          return;
        }

        var newFileDetails = this.addToNewFileList(file);
         newFileDetailsCollection.push(newFileDetails);

        //console.log(`item.name = ${file?.name}`);
      }
      else if (item.kind === "string") {
        if (item.type === "text/plain") {
          // only process the plain text versions of the strings
          
          // And build a .txt file from it

          item.getAsString(async (s) => {
            var fileContents = s;

            var file = new File([fileContents], "New Text Document.txt")
            var newFileDetails = this.addToNewFileList(file);
            newFileDetailsCollection.push(newFileDetails);

            //console.log(`item.text = ${fileContents}`);
            
          });
        }
      }
    }

    newFileDetailsCollection.forEach(async x => await this.calculateFileHash(x));
  }

  async onFileChosen(event: any) {
    var newFiles = [...event.target.files] as File[];

    var newFileDetailsCollection: NewFileDetails[] = [];
    for (let newFile of newFiles) {
      var newFileDetails = this.addToNewFileList(newFile);
      newFileDetailsCollection.push(newFileDetails);
    }

    newFileDetailsCollection.forEach(async x => await this.calculateFileHash(x));
  }

  addToNewFileList(file: File): NewFileDetails {
    // Remove the file if it has already been added previously
    this.newFiles = this.newFiles.filter(x => x.name !== file.name);

    var newFileDetails = new NewFileDetails(file);
    this.newFiles.push(newFileDetails);

    return newFileDetails;
  }

  async calculateFileHash(newFileDetails: NewFileDetails) {
    newFileDetails.fileHash = await this.getFileMD5Hash(newFileDetails.file);
  }

  async getFileMD5Hash(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      const spark = new SparkMD5.ArrayBuffer();
  
      // NOTE: this could be updated to use SparkMD5's streaming API if the file sizes get too big.
      fileReader.onload = (event) => {
        if (event.target?.result) {
          spark.append(event.target.result as ArrayBuffer);
          const hash = spark.end();
          resolve(hash);
        } else {
          reject(new Error("FileReader failed to read the file."));
        }
      };
  
      fileReader.onerror = () => {
        reject(new Error("An error occurred while reading the file."));
      };
  
      // Read the file as an ArrayBuffer.
      fileReader.readAsArrayBuffer(file);
    });
  }


  getFileExtention(filename: string): string {
    var index = filename.lastIndexOf('.');
    var extension = filename.slice(index + 1);
    return extension;
  }

  canShowAsImage(file: FileVaultReferenceRelation): boolean {
    if (!file.fileHash) {
      // File hash is not set, so we cannot load it as an image
      // It was probably just uploaded.
      return false;
    }

    var extension = this.getFileExtention(file.fileName!).toLowerCase();

    return extension === 'jpg' ||
           extension === 'jpeg' ||
           extension === 'webp' ||
           extension === 'svg' ||
           extension === 'png' ||
           extension === 'gif' ||
           extension === 'avif' ||
           extension === 'apng';
  }

  canShowAsText(file: FileVaultReferenceRelation): boolean {
    if (!file.fileHash) {
      // File hash is not set, so we cannot load it just yet
      // It was probably just uploaded.
      return false;
    }

    var extension = this.getFileExtention(file.fileName!).toLowerCase();

    return extension === 'txt';
  }

  async loadValidExtensions() {
    this.loading++;
    try {
      this.validFileExtensions = await this.situViewClient.getValidFileExtensions(this.fileVaultCategory);
    }
    finally {
      this.loading--;
    }
  }

  async loadFiles() {
    this.loading++;

    var request = new GetFileVaultReferenceRelationsRequest();
    request.companyId = this.product.companyId;
    request.subjectId = this.product.id;
    request.category = this.fileVaultCategory;

    try {
      var requestResponse = await this.situViewClient.getFileVaultReferenceRelations(request);

      if (!requestResponse.success) {
        this.showMessage("Failed to load files");
        return;
      }

      var existingFiles = requestResponse.fileVaultReferenceRelations! as ExistingFileDetails[];
      existingFiles.slice(0, 5).forEach(x => x.autoLoadAssetValidation = true);
      this.existingFiles = existingFiles;
      
      for (let existingFile of this.existingFiles) {
        // Load any text documents
        if (existingFile.fileSize && existingFile.fileSize <= 1024 && this.canShowAsText(existingFile)) {
          this.fetchText(existingFile);
        }
      }
      
    }
    finally {
      this.loading--;
    }
  }

  async download(file: FileVaultReferenceRelation) {
    this.loading++;

    try {
      var fileResponse = await this.situViewClient.getFile(file.companyId, file.id);

      FileSaver.saveAs(fileResponse.data, file.fileName);
    }
    finally {
      this.loading--;
    }
  }

  async downloadZip(files: FileVaultReferenceRelation[]) {
    this.loading++;

    try {
      var request = new GetZippedFilesRequest();
      request.fileVaultReferences = files.map(x => {
        const pair = new CompanyProductIdPair();
        pair.companyId = x.companyId!;
        pair.fileId = x.id!;
        return pair;
      });
      var fileResponse = await this.situViewClient.getZippedFiles(request);

      var fileName = files.length === 1 ? files[0].fileName! : `${this.product.name} files`;
      fileName = `${fileName}.zip`
      FileSaver.saveAs(fileResponse.data, fileName);
    }
    finally {
      this.loading--;
    }
  }

  async uploadBlob(fileDetails: NewFileDetails, skipHashCheck = false) {
    this.loading++;

    try {
      var fileVaultUploadRequest = new FileVaultUploadRequest();
      fileVaultUploadRequest.companyId = this.product.companyId;
      fileVaultUploadRequest.subjectId = this.product.id;
      fileVaultUploadRequest.fileName = fileDetails.name;
      fileVaultUploadRequest.category = this.fileVaultCategory;
      fileVaultUploadRequest.fileHash = skipHashCheck ? undefined : fileDetails.fileHash;

      var requestUploadResponse = await this.situViewClient.requestFileVaultUpload(fileVaultUploadRequest);

      if (!requestUploadResponse.success) {
        var msg = requestUploadResponse.errorMessages?.find(_ => true);
        if (msg) {
          msg = `Failed to request file upload: ${msg}`;
        } else {
          msg = "Failed to request file upload.";
        }

        throw new Error(msg);
      }

      if (requestUploadResponse.fileAlreadyInFileVault && requestUploadResponse.fileVaultReferenceId) {
        // This file has been marked as being in the file fault already
        // Ask the user if they want to upload the file, or use an existing file instead.

        this.confirmDialogService.open({
          title: 'File Already Exists',
          message: `A file identical to '${fileDetails.name}' has already been uploaded. Would you like to continue the file upload, or select an existing file instead?`,
          cancelText: 'Cancel Upload',
          confirmText: 'Upload As New File',
          secondOptionText: 'Select From Existing Files',
          secondOptionActionIcon: 'find_replace',
          actionIcon: 'upload',
          actionColour: 'accent'
        } as ConfirmDialogData);

        var dialogResult = await this.confirmDialogService.confirmed().toPromise();

        if (dialogResult === false) {
          // The user has chosen to cancel, do nothing
        } else if  (dialogResult === undefined) {
          // The user has chosen to select an exising file, open the file browser filtered by the file hash
          var addedExistingFile = await this.onAddExistingReferenceItemClick(fileDetails.fileHash);
          if (addedExistingFile) {
            this.newFiles = this.newFiles.filter(x => x !== fileDetails);
          }
        } else {
          // The user has chosen to upload the file, request a file upload without the hash
          await this.uploadBlob(fileDetails, true);
        }
        
        return;
      }

      var uploadResponse = await fetch(requestUploadResponse.uploadUrl!, {
        method: 'PUT',
        headers: {
          'Content-Type': `${fileDetails.file.type}`,
          'x-ms-blob-content-type': `${fileDetails.file.type}`,
          'x-ms-blob-type': 'BlockBlob'
        },
        body: fileDetails.file,
      });
      
      if (!uploadResponse.ok) {
        // Upload completed
        throw new Error("Failed to upload file.");
      }

      var fileVaultUploadCommitRequest = new FileVaultUploadCommitRequest();
      fileVaultUploadCommitRequest.companyId = this.product.companyId;
      fileVaultUploadCommitRequest.fileVaultReferenceId = requestUploadResponse.fileVaultReferenceId;
      var uploadCommitResponse = await this.situViewClient.commitFileVaultUpload(fileVaultUploadCommitRequest);

      if (!uploadCommitResponse.success) {
        // Upload completed
        throw new Error("Failed to commit file.");
      }

      var existingFile = uploadCommitResponse.fileVaultReferenceRelation! as ExistingFileDetails;
      existingFile.autoLoadAssetValidation = true;

      this.newFiles = this.newFiles.filter(x => x !== fileDetails);
      this.existingFiles = this.existingFiles.filter(x => x.id !== requestUploadResponse.fileVaultReferenceId);
      this.existingFiles.unshift(existingFile);

    } catch (e: any) {
      this.showMessage(e.message);
    }
    finally {
      this.loading--;
    }

  }

  async downloadSelected() {
    for (let existingFile of this.existingFiles) {
      if (existingFile.isSelected) {
        await this.download(existingFile);
      }
    }
  }

  async downloadSelectedZip() {
    var selectedFiles = this.existingFiles.filter(x => x.isSelected);
    await this.downloadZip(selectedFiles);
  }

  async uploadSelected() {
    for (let newFile of this.newFiles) {
      if (newFile.isSelected) {
        await this.uploadBlob(newFile);
      }
    }
  }

  formatFileSize(fileSize: number) {
    if (fileSize < 1024) return fileSize + ' B';
    let kb = fileSize / 1024;
    if (kb < 1024) return kb.toFixed(2) + ' KB';
    let mb = kb / 1024;
    if (mb < 1024) return mb.toFixed(2) + ' MB';
    let gb = mb / 1024;
    return gb.toFixed(2) + ' GB';
}

  hasNewFilesSelected() {
    return this.newFiles.some(x => x.isSelected && x.fileHash);
  }

  hasExistingFilesSelected() {
    return this.existingFiles.some(x => x.isSelected);
  }

  hasSelectedAllNewFiles() {
    return this.newFiles.every(x => x.isSelected);
  }

  selectAllNewFiles(select: boolean) {
    this.newFiles.forEach(async x => x.isSelected = select);
  }

  hasSelectedAllExistingFiles() {
    return this.existingFiles.every(x => x.isSelected);
  }

  selectAllExistingFiles(select: boolean) {
    this.existingFiles.forEach(x => x.isSelected = select);
  }

  getFileDownloadUrl(file: FileVaultReferenceRelation) {
    var url = `${this.SituViewUrl}/api/commands/experimental/FileVault/GetFile?companyId=${file.companyId}&fileVaultReferenceId=${file.id}`;
    return url;
  }

  fetchText(file: ExistingFileDetails) {
    var url = this.getFileDownloadUrl(file);
    this.http.get(url, { responseType: 'text' }).subscribe(
      data => file.content = data,
      error => console.error('Error fetching text:', error)
    );
  }

  addSelectedFilesToAdditionalProductsDialog() {
    var selectedFiles = this.existingFiles.filter(x => x.isSelected)
    this.addFilesToAdditionalProductsDialog(selectedFiles);
  }

  addFileToAdditionalProductsDialog(file: ExistingFileDetails) {
    this.addFilesToAdditionalProductsDialog([file]);
  }
  
  addFilesToAdditionalProductsDialog(files: ExistingFileDetails[]) {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
  
    const dialogWidth = `${Math.round((70 / 100) * screenWidth)}px`;
    const dialogHeight = `${Math.round((90 / 100) * screenHeight)}px`;

    var dialogData = new ProductSelectorDialogData();
    dialogData.companyId = this.selectedCompany?.id;
    dialogData.multiSelect = true;
    dialogData.tagSelectionEnabled = false;

    this.productSelectorDialogRef = this.dialog.open(ProductSelectorDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: dialogData,
    });

    this._subscriptions.push(this.productSelectorDialogRef.afterClosed()
      .subscribe(async (data: ProductSelectorResultData) => {
        if (data?.products) {

          var addReferencesRequest = new AddFileVaultReferencesRequest();
          addReferencesRequest.fileVaultReferenceIds = files.map(x => x.id!);
          addReferencesRequest.category = this.fileVaultCategory;
          addReferencesRequest.subjects = data.products.map(x => {
            var companySubjectPair = new CompanySubjectPair;
            companySubjectPair.companyId = x.companyId;
            companySubjectPair.subjectId = x.id;
            return companySubjectPair;
          });

          var addReferenceResponse = await this.situViewClient.addFileVaultReferences(addReferencesRequest);

          if (!addReferenceResponse.success) {
            this.showMessage("Failed to add file to other products");
          } else {
            this.showMessage(`Added ${addReferenceResponse.fileVaultReferences!.length} ${this.pluralize("file", addReferenceResponse.fileVaultReferences!.length)}`);
          }
          
        }
      }));
  
  }

  async removeSelectedFiles() {
    this.confirmDialogService.open({
      title: 'Remove Selected Files',
      message: 'Are you sure you wish to remove the selected files?',
      cancelText: 'No',
      confirmText: 'Yes',
      actionIcon: 'delete',
      actionColour: 'warn'
    } as ConfirmDialogData);

    this.confirmDialogService.confirmed().subscribe(async confirmed => {
      if (confirmed) {
        for (var file of this.existingFiles) {
          if (file.isSelected) {
            this.removeFileRequest(file);
          }
        }
    
        this.loadFiles();
      }
    });
  }

  removeFile(file: ExistingFileDetails) {

    this.confirmDialogService.open({
      title: 'Remove File',
      message: `Are you sure you wish to remove '${file.fileName}'?`,
      cancelText: 'No',
      confirmText: 'Yes',
      actionIcon: 'delete',
      actionColour: 'warn'
    } as ConfirmDialogData);

    this.confirmDialogService.confirmed().subscribe(async confirmed => {
      if (confirmed) {
        await this.removeFileRequest(file);
        await this.loadFiles();
      }
    });
  }

  async removeFileRequest(file: ExistingFileDetails, force: boolean = false) {
    var removeReferencesRequest = new RemoveFileVaultReferencesRequest();
    removeReferencesRequest.fileVaultReferenceId = file.id;
    removeReferencesRequest.category = this.fileVaultCategory;
    removeReferencesRequest.force = force;

    var companySubjectPair = new CompanySubjectPair;
    companySubjectPair.companyId = this.product.companyId;
    companySubjectPair.subjectId = this.product.id;

    removeReferencesRequest.subjects = [companySubjectPair];

    var removeReferenceResponse = await this.situViewClient.removeFileVaultReferences(removeReferencesRequest);

    if (!removeReferenceResponse.success) {

      if (removeReferenceResponse.wouldRemoveLastReference) {

        this.confirmDialogService.open({
          title: 'Permanently Delete File',
          message: `This is the final reference to this file. Are you sure you wish to permanently delete '${file.fileName}'?`,
          cancelText: 'No',
          confirmText: 'Yes',
          actionIcon: 'delete',
          actionColour: 'warn'
        } as ConfirmDialogData);
    
        this.confirmDialogService.confirmed().subscribe(async confirmed => {
          if (confirmed) {
            await this.removeFileRequest(file, true);
            await this.loadFiles();
          }
        });

      } else {
        this.showMessage("Failed to remove file from product");
      }

    } else {
      this.showMessage(`Removed file from product`);
    }
  }

  isValidExtension(file: NewFileDetails) {
    var extension = file.extension.toLowerCase();
    var result = this.validFileExtensions.some(x => x === extension);
    return result;
  }

  pluralize(unit: string, value: number): string {
    return Utils.pluralize(unit, value);
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

class ExistingFileDetails extends FileVaultReferenceRelation {
  isSelected = false;
  content?: string;
  autoLoadAssetValidation = false;
}

class NewFileDetails {
  file: File;

  isSelected = true;

  nameWithoutExtension = "";
  extension = "";

  fileHash?: string = undefined;

  public get name(): string {
    return `${this.nameWithoutExtension}.${this.extension}`
  }
  public set name(value: string) {
    var index = value.lastIndexOf('.');
    this.nameWithoutExtension = value.slice(0, index);
    this.nameWithoutExtension = this.sanitizeFilename(this.nameWithoutExtension);
    this.extension = value.slice(index + 1);
  }

  editingName?: string = undefined;

  constructor(file: File) {
    this.file = file;
    this.name = file.name;
  }

  sanitizeFilename(filename: string) : string {
    var maxFileLenth = 254 - this.nameWithoutExtension.length;
    if (filename.length > maxFileLenth) {
      filename = filename.slice(0, maxFileLenth);
    }

    return filename.replace(/[/\\?%*:|"<>]/g, '_');
  }

  getFileExtention(): string {
    var index = this.name.lastIndexOf('.');
    var extension = this.name.slice(index + 1);
    return extension;
  }

  updateFileName(filename: string) {
    filename = `${filename}.${this.getFileExtention()}`
    var newName = this.sanitizeFilename(filename);
    this.name = newName;
  }

  enableNameEdit() {
    this.editingName = this.nameWithoutExtension;
  }

  saveNameEdit() {
    this.nameWithoutExtension = this.sanitizeFilename(this.editingName!);
    this.editingName = undefined;
  }
}